"use client";

import { useState } from "react";
import type { ProfileData } from "@/types/profile";
import { FOUNDER_BADGE_LABEL, isFounderProfileType } from "@/lib/founders/founderPlan";
import { Badge } from "@/components/ui/badge";
import {
  Award,
  Briefcase,
  FileBadge,
  GraduationCap,
  ShieldCheck,
  UtensilsCrossed,
  Wine,
  Flame,
  Sparkles,
  Star,
  Plus,
} from "lucide-react";
import {
  BADGE_PRIORITY,
  capBadgesForDisplay,
  getEarnedTrustBadges,
  type TrustBadgeBundle,
} from "@/lib/verification/verificationRequirements";
import { isVerificationStatusVerified } from "@/lib/verification/verificationStatus";
import { resolveProviderBadgeTier, PROVIDER_TIER_DESCRIPTION, PROVIDER_TIER_LABEL, PROVIDER_TIER_STYLE } from '@/lib/verification/providerBadgeTier';

/**
 * Public trust badges on a profile card.
 *
 * ## Ordering
 *
 * Badges are ordered by what a customer is actually deciding with, not by the
 * order they happen to appear in the profile document:
 *
 *   1. Identity       — who is this person
 *   2. Business       — does the business exist
 *   3. Health & safety — does it meet the standard a customer assumes
 *   4. Bundles        — grouped outcomes (a clean kitchen, a licensed bar)
 *   5. Trade          — does this contractor hold MY trade's certificate
 *   6. Everything else
 *
 * Fix-it trade certificates sit above the generic tail because a homeowner
 * searching for a plumber is reading the strip for "Plumbing", not "Red Seal".
 */

const BUNDLE_ICONS: Record<string, typeof ShieldCheck> = {
  safe_clean_kitchen: UtensilsCrossed,
  licensed_bar: Wine,
  fire_safe: Flame,
  certified_kitchen: Sparkles,
  star_graded: Star,
};

/** Icon + colour for each bundle so they read as distinct achievements. */
const BUNDLE_STYLE: Record<string, string> = {
  safe_clean_kitchen: 'bg-teal-600 hover:bg-teal-700',
  licensed_bar: 'bg-purple-600 hover:bg-purple-700',
  fire_safe: 'bg-orange-600 hover:bg-orange-700',
  certified_kitchen: 'bg-indigo-600 hover:bg-indigo-700',
  star_graded: 'bg-amber-500 hover:bg-amber-600',
};

interface TrustBadge {
  key: string;
  label: string;
  icon: React.ReactNode;
  className: string;
  priority: number;
  description?: string;
}

export function VerificationBadges({ profileData }: { profileData: ProfileData }) {
  const showFounderTrustBadge =
    Boolean(profileData.founderBadgeActive) || isFounderProfileType(profileData.profileType);

  const manual = Array.isArray(profileData.manualBadges) ? profileData.manualBadges : [];
  /**
   * The provider tier, derived from live verification state.
   *
   * Never stored. A stored tier is a snapshot that has to be written on
   * approval and walked back on expiry - and the expiry sweep already
   * downgrades individual status fields, so a stored tier would need a second
   * downgrade path. Deriving it means the tier drops the same day a document
   * lapses, with no extra code to remember.
   */
  const providerTier = resolveProviderBadgeTier(profileData);

  /**
   * Admins write display strings ("ID Verified", "CIPC Registered") as well as
   * plain "verified", so every status must go through the tolerant verifier.
   * Comparing to the literal 'Verified' silently hid badges for manually
   * granted credentials.
   */
  const granted = (status: unknown, label: string) =>
    isVerificationStatusVerified(status) || manual.includes(label);

  const identity = (status: unknown, label: string, icon: React.ReactNode, className: string, priority: number, description?: string): TrustBadge | null =>
    granted(status, label) ? { key: label, label, icon, className, priority, description } : null;

  const badges: Array<TrustBadge | null> = [
    showFounderTrustBadge
      ? {
          key: 'founder',
          label: profileData.founderBadgeLabel || FOUNDER_BADGE_LABEL,
          icon: <Award className="h-3 w-3 mr-1" />,
          className: 'bg-amber-600 hover:bg-amber-700',
          priority: 0,
        }
      : null,

    // 1. The provider tier, if earned. Highest-priority badge, because it is
    // the summary of everything below it.
    providerTier !== 'none'
      ? {
          key: 'provider-tier',
          label: PROVIDER_TIER_LABEL[providerTier],
          icon: <Award className="h-3 w-3 mr-1" />,
          className: PROVIDER_TIER_STYLE[providerTier],
          priority: BADGE_PRIORITY.identity,
          description: PROVIDER_TIER_DESCRIPTION[providerTier],
        }
      : null,

    // 2. Background checked - optional, and deliberately outside the tier.
    // A screening result is a trust add-on, not a step on the progression, and
    // nobody is penalised for not having one.
    identity(
      profileData.huru_background_check_status,
      'Background Checked',
      <ShieldCheck className="h-3 w-3 mr-1" />,
      'bg-indigo-600 hover:bg-indigo-700',
      BADGE_PRIORITY.bundle,
      'Holds a background-check report. Optional, and not a safety guarantee.',
    ),

    // 1. Identity
    identity(profileData.id_document_status, 'ID Verified', <ShieldCheck className="h-3 w-3 mr-1" />, 'bg-green-600 hover:bg-green-700', BADGE_PRIORITY.identity, 'Identity confirmed'),

    // 3. Business existence
    identity(profileData.cipc_registration_status, 'CIPC Registered', <Briefcase className="h-3 w-3 mr-1" />, 'bg-green-600 hover:bg-green-700', BADGE_PRIORITY.business, 'Registered company'),
    identity(profileData.sole_prop_business_proof_status, 'Sole Proprietor Business Verified', <Briefcase className="h-3 w-3 mr-1" />, 'bg-green-600 hover:bg-green-700', BADGE_PRIORITY.business, 'Trading as an individual'),

    // 4. Health & safety — the standard a diner or guest assumes is met
    identity(profileData.coa_certificate_status, 'Health Verified (COA)', <ShieldCheck className="h-3 w-3 mr-1" />, 'bg-amber-600 hover:bg-amber-700', BADGE_PRIORITY.healthAndSafety, 'Passed a municipal health inspection'),

    // 5. Grouped outcomes
    ...getEarnedTrustBadges(profileData).map((bundle: TrustBadgeBundle) => {
      const Icon = BUNDLE_ICONS[bundle.key] ?? ShieldCheck;
      return {
        key: `bundle:${bundle.key}`,
        label: bundle.label,
        icon: <Icon className="h-3 w-3 mr-1" />,
        className: BUNDLE_STYLE[bundle.key] ?? 'bg-green-600 hover:bg-green-700',
        priority: BADGE_PRIORITY.bundle,
        description: bundle.description,
      } satisfies TrustBadge;
    }),

    // 6. Trade certificates — kept separate so a homeowner sees their own trade
    identity(profileData.pirb_registration_status, 'PIRB Registered', <ShieldCheck className="h-3 w-3 mr-1" />, 'bg-green-600 hover:bg-green-700', BADGE_PRIORITY.trade),
    identity(profileData.plumbing_coc_status, 'Plumbing COC Issuer', <ShieldCheck className="h-3 w-3 mr-1" />, 'bg-green-600 hover:bg-green-700', BADGE_PRIORITY.trade),
    identity(profileData.electrical_coc_status, 'Electrical COC Issuer', <ShieldCheck className="h-3 w-3 mr-1" />, 'bg-green-600 hover:bg-green-700', BADGE_PRIORITY.trade),
    identity(profileData.gas_coc_status, 'Gas COC Issuer', <ShieldCheck className="h-3 w-3 mr-1" />, 'bg-green-600 hover:bg-green-700', BADGE_PRIORITY.trade),
    identity(profileData.nhbrc_certificate_status, 'NHBRC Certified', <ShieldCheck className="h-3 w-3 mr-1" />, 'bg-green-600 hover:bg-green-700', BADGE_PRIORITY.trade),
    identity(profileData.saqcc_gas_practitioner_status, 'SAQCC Gas Practitioner', <ShieldCheck className="h-3 w-3 mr-1" />, 'bg-green-600 hover:bg-green-700', BADGE_PRIORITY.trade),
    identity(profileData.electric_fence_coc_status, 'Electric Fence COC Issuer', <ShieldCheck className="h-3 w-3 mr-1" />, 'bg-green-600 hover:bg-green-700', BADGE_PRIORITY.trade),
    identity(profileData.water_installation_certificate_status, 'Water Installation Certified', <ShieldCheck className="h-3 w-3 mr-1" />, 'bg-green-600 hover:bg-green-700', BADGE_PRIORITY.trade),
    identity(profileData.repssa_registration_status, 'REPSSA Registered', <ShieldCheck className="h-3 w-3 mr-1" />, 'bg-green-600 hover:bg-green-700', BADGE_PRIORITY.trade),

    // 6. Everything else
    identity(profileData.sars_clearance_status, 'Tax Compliance Status (TCS)', <FileBadge className="h-3 w-3 mr-1" />, 'bg-green-600 hover:bg-green-700', BADGE_PRIORITY.tax),
    identity(profileData.public_liability_insurance_status, 'Public Liability Insured', <ShieldCheck className="h-3 w-3 mr-1" />, 'bg-green-600 hover:bg-green-700', BADGE_PRIORITY.other),
    identity(profileData.qualification_certificate_status, 'Qualification Verified', <GraduationCap className="h-3 w-3 mr-1" />, 'bg-green-600 hover:bg-green-700', BADGE_PRIORITY.other),
    identity(profileData.red_seal_certificate_status, 'Red Seal Certified', <Award className="h-3 w-3 mr-1" />, 'bg-green-600 hover:bg-green-700', BADGE_PRIORITY.other),
    identity(profileData.ffc_certificate_status, 'FFC Certified', <ShieldCheck className="h-3 w-3 mr-1" />, 'bg-green-600 hover:bg-green-700', BADGE_PRIORITY.other),
    identity(profileData.fsp_license_status, 'FSP Licensed', <ShieldCheck className="h-3 w-3 mr-1" />, 'bg-green-600 hover:bg-green-700', BADGE_PRIORITY.other),
    identity(profileData.sars_pr_number_status, 'SARS Practitioner', <FileBadge className="h-3 w-3 mr-1" />, 'bg-green-600 hover:bg-green-700', BADGE_PRIORITY.other),
  ];

  const earned = badges
    .filter((badge): badge is TrustBadge => Boolean(badge))
    .sort((a, b) => a.priority - b.priority);

  if (earned.length === 0) return null;

  return (
    <TrustBadgeStrip badges={earned} />
  );
}

/**
 * Paints at most DISPLAY_BADGE_CAP badges and collapses the rest behind a
 * "+N more" toggle.
 *
 * The cap exists because a busy Fix-it contractor can legitimately hold nine
 * trade certificates. Eleven coloured pills across a search-result card reads
 * as noise and pushes the profile name out of the visual hierarchy — the strip
 * stops communicating anything at all. The badge remains available one click
 * away, so nothing is hidden, only folded.
 */
function TrustBadgeStrip({ badges }: { badges: TrustBadge[] }) {
  const [expanded, setExpanded] = useState(false);

  const { visible, hiddenCount: hidden } = expanded
    ? { visible: badges, hiddenCount: 0 }
    : capBadgesForDisplay(badges);

  return (
    <div className="flex flex-wrap items-center justify-center gap-2 mt-2">
      {visible.map((badge) => (
        <Badge
          key={badge.key}
          variant="default"
          title={badge.description}
          className={`text-xs flex items-center text-white ${badge.className}`}
        >
          {badge.icon}
          {badge.label}
        </Badge>
      ))}

      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="rounded-full border border-slate-300 bg-white px-2 py-0.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
          aria-label={`Show ${hidden} more verification badge${hidden === 1 ? '' : 's'}`}
        >
          <Plus className="mr-0.5 inline h-3 w-3" />+{hidden} more
        </button>
      )}

      {expanded && hidden === 0 && badges.length > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="rounded-full border border-slate-300 bg-white px-2 py-0.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
        >
          Show less
        </button>
      )}
    </div>
  );
}